import {
  OdProtosCustomerV1Data_PreferenceSource,
  UpsertMarketingSmsConsentDocument,
  UserSessionDocument,
} from './__generated__/graphql';
import type { GraphQLClient } from './graphql/client';

/**
 * The subset of `OdProtosCustomerV1Data_PreferenceSource` the auth-fe account
 * form records under, kept as a literal union so consumers pass plain strings
 * and only sanctioned audit sources compile. Add a value here when a new host
 * surface adopts the V2 form and needs a distinct audit source.
 */
export type PreferenceSource = 'PREFERENCE_SOURCE_COSMOS';

export interface MarketingSmsConsent {
  /** The user's checkbox value: `true` opts in, `false` opts out. */
  optIn: boolean;
  /**
   * The email the account was just registered with. The write is attributed to
   * whatever the session read resolves, so this guards against landing the
   * decision on the wrong customer: if a session swap (e.g. another tab
   * re-authenticating as someone else between register and this read) means the
   * resolved human is different, the write is skipped rather than written to
   * that human's audit trail.
   */
  email: string;
  /** Versioned key naming the exact consent copy the user saw, for the audit trail. */
  consentLanguage: string;
  /** Audit source identifying the surface the consent decision came from. */
  preferenceSource: PreferenceSource;
}

export type UpsertMarketingSmsConsentResult =
  | { success: true }
  // `no-customer`: the session read succeeded but resolved no customer to
  // attribute the consent to, so nothing was written (never guess an identity).
  // `error`: the read or write failed (transient, a GraphQL error, or a write
  // that persisted nothing) — kept distinct from `no-customer` so a caller can
  // tell a fetch failure apart from the legitimate "not authenticated yet" state.
  | { success: false; reason: 'no-customer' | 'error' };

/**
 * Records a freshly-registered customer's marketing-SMS preference. Resolves
 * the customer via the session read (GetHuman — the same `userSession` query
 * the provider already runs), then writes the durable `Consent` record through
 * `customerCommPreferences.upsertCommPreference` on the Go customer service
 * (not Rails, not opencomm). Requires the authenticated session `register`
 * established, hence the register → GetHuman → upsert sequencing.
 *
 * The customer service seeds marketing SMS opted-OUT
 * (`DefaultCommPreferenceMediumPreferenceChanges`), so this write exists to
 * record the user's explicit decision on the audit trail regardless of value:
 * `optIn` checked → `marketingSmsOptOut: false`, unchecked → `true`. Only
 * `marketingSmsOptOut` is sent (no `smsOptOut`), matching the legacy cosmos
 * login write it replaces.
 *
 * The decision is attributed only to the account named by `email`: if the
 * session read resolves a different human (a mid-flight session swap), the
 * write is skipped rather than misattributed.
 *
 * Client contract — REQUIRES A NETWORK-ONLY / UNCACHED client. This re-reads
 * `userSession` to pick up the customer the just-completed `register` created;
 * a cache-first client (e.g. RTK Query) would serve the mount-time session
 * (fired pre-register → null, or a stale prior session) and attribute the
 * consent to the wrong customer or none. Cosmos (the current live consumer)
 * wires a raw uncached fetch client. A future cached-client consumer (e.g.
 * reception-fe, whose RTK Query cache is first) must force network-only here.
 *
 * Best-effort by contract: never throws. Registration has already succeeded by
 * the time this runs, so a failed consent write must not surface as a
 * registration failure — it resolves to a typed failure the caller observes
 * (the provider logs it via `trackError`).
 */
export async function upsertMarketingSmsConsent(
  client: GraphQLClient,
  { optIn, email, consentLanguage, preferenceSource }: MarketingSmsConsent,
): Promise<UpsertMarketingSmsConsentResult> {
  let customerUuid: string | undefined;
  try {
    const session = await client.query(UserSessionDocument, {});
    // The client surfaces failures in-band (`{ data, error }`), so a resolved
    // error is a fetch failure, not "no customer".
    if (session.error) {
      return { success: false, reason: 'error' };
    }
    const human = session.data?.web?.userSession?.human;
    // Only attribute the consent to the account that just registered. A session
    // that resolves to a different human (a swap between register and this read)
    // is treated as no-customer so the decision never lands on someone else's
    // audit trail.
    if (!emailsMatch(human?.email, email)) {
      return { success: false, reason: 'no-customer' };
    }
    customerUuid = human?.customer_uuid ?? undefined;
  } catch {
    return { success: false, reason: 'error' };
  }
  if (!customerUuid) {
    return { success: false, reason: 'no-customer' };
  }

  try {
    const response = await client.query(UpsertMarketingSmsConsentDocument, {
      input: {
        customerUuid,
        marketingSmsOptOut: !optIn,
        consentLanguage,
        preferenceSource: OdProtosCustomerV1Data_PreferenceSource[preferenceSource],
      },
    });
    if (response.error) {
      return { success: false, reason: 'error' };
    }
    // A response with no error but no persisted `commPreference` is a silent
    // no-op (or an unsurfaced authz denial), not a successful write.
    if (!response.data?.customerCommPreferences?.upsertCommPreference?.commPreference) {
      return { success: false, reason: 'error' };
    }
    return { success: true };
  } catch {
    return { success: false, reason: 'error' };
  }
}

/** Case-insensitive email comparison; a missing session email never matches. */
function emailsMatch(sessionEmail: string | null | undefined, registeredEmail: string): boolean {
  return (
    sessionEmail != null &&
    sessionEmail.trim().toLowerCase() === registeredEmail.trim().toLowerCase()
  );
}
